import { tinyButtons } from '@byecode/ui'
import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
    type ActorEvent,
    ARRANGEMENT,
    type Arrangement,
    type FieldADTValue,
    type Pagination,
    type RecordLikeProtocol,
    type SizeConfigure
} from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { getRecordPoolKey, useAtomData, useFlowLayoutContext } from '@lighthouse/shared'
import { mergeRefs } from '@lighthouse/tools'
import { useUpdateEffect } from '@react-hookz/web'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useStore } from 'jotai'
import React, { forwardRef, memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { calculateNewSortValue, getEffectiveSortValue } from '../shared/sortUtils'
import { Item } from './Item'
import { setDeadCopyNode } from './utils'

const ScrollArea = styled.div`
    overflow: auto visible;
    /* ${tinyButtons} */
    &::-webkit-scrollbar {
        display: none;
    }
`

const Root = styled.div<{ gap: number }>`
    display: flex;
    flex-wrap: nowrap;
`

interface Props extends React.ComponentPropsWithoutRef<'div'> {
    node: FlowLayoutNode
    anchorId?: string
    itemWidth: number
    title: string
    cols: number
    gap: number
    readonly?: boolean
    canPaginate?: boolean
    pagination: Pagination
    scale?: number
    arrangement: Arrangement
    enableDrag?: boolean
    sortId?: string
    onLoadMoreData?: (pageNum: number) => Promise<RecordLikeProtocol[]>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onUpdateRecord?: (recordId: string, content: RecordLikeProtocol['content']) => Promise<RecordLikeProtocol>
}

interface SortableHorizontalItemProps {
    id: string
    disabled?: boolean
    style?: React.CSSProperties
    children: React.ReactNode
    measureRef?: (node: HTMLElement | null) => void
    dataIndex?: number
    dataViewItem?: number
}

const SortableHorizontalItem = memo<SortableHorizontalItemProps>(
    ({ id, disabled, style: baseStyle, children, measureRef, dataIndex, dataViewItem }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })

        const style: React.CSSProperties = {
            ...baseStyle,
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
            cursor: disabled ? 'pointer' : isDragging ? 'grabbing' : 'grab'
        }

        return (
            <div
                ref={node => {
                    setNodeRef(node)
                    measureRef?.(node)
                }}
                data-index={dataIndex}
                data-view-item={dataViewItem}
                style={style}
                {...attributes}
                {...listeners}
            >
                {children}
            </div>
        )
    }
)

export const HorizontalScrollView = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const {
            node,
            anchorId,
            itemWidth,
            title,
            cols,
            gap,
            readonly,
            canPaginate,
            pagination,
            scale = 1,
            arrangement,
            enableDrag,
            sortId,
            onLoadMoreData,
            onRecordClick,
            onUpdateRecord,
            ...rest
        } = props

        const { currentPage = 1, pageSize = 10, rowTotal = 0 } = pagination
        const overscan = Math.ceil(pageSize / 5)

        const { recordPoints, blockData, recordPoolAtom, dataSource } = useViewBlockContext()
        const { id: blockId } = blockData
        const { id: dsId, schema } = dataSource
        const isFitArrangement = arrangement === ARRANGEMENT.fit

        const store = useStore()

        // selector 返回数组而非 Map，因为 useAtomData 使用 fast-deep-equal（基础版）不支持 Map 比较
        const sortedRecordPoints = useAtomData(
            recordPoolAtom,
            useCallback(
                pool => {
                    if (!enableDrag) {
                        return recordPoints
                    }
                    return [...recordPoints].sort((a, b) => {
                        const recordA = pool.get(getRecordPoolKey(dsId, a.id))
                        const recordB = pool.get(getRecordPoolKey(dsId, b.id))
                        if (!recordA || !recordB) {
                            return 0
                        }
                        return getEffectiveSortValue(recordA, sortId) - getEffectiveSortValue(recordB, sortId)
                    })
                },
                [enableDrag, recordPoints, dsId, sortId]
            )
        )

        const effectiveRecordPoints = enableDrag ? sortedRecordPoints : recordPoints

        const innerRef = useRef<HTMLDivElement | null>(null)
        const loadingRef = useRef(false)
        const virtualizer = useVirtualizer({
            horizontal: true,
            count: effectiveRecordPoints.length,
            getItemKey: i => effectiveRecordPoints[i].id,
            getScrollElement: () => innerRef.current,
            estimateSize: () => (innerRef.current?.clientWidth ?? 1200) * (1 / cols),
            overscan,
            gap
        })

        const latest = useLatest({ canPaginate })

        const inRange = currentPage < Math.ceil(rowTotal / pageSize)
        const isReachingTheRight = virtualizer.range ? virtualizer.range.endIndex >= recordPoints.length - 1 - overscan : false
        const hasMore = rowTotal > recordPoints.length
        const canLoadMore = inRange && isReachingTheRight && hasMore

        useUpdateEffect(() => {
            void (async () => {
                if (loadingRef.current || !latest.current.canPaginate) {
                    return
                }

                if (canLoadMore && onLoadMoreData) {
                    loadingRef.current = true
                    await onLoadMoreData(currentPage + 1)

                    loadingRef.current = false
                }
            })()
        }, [currentPage, canLoadMore, onLoadMoreData])

        const itemHeightRef = useRef<Map<string | number | bigint, HTMLElement | null>>(new Map())
        const items = virtualizer.getVirtualItems()

        const [currentMaxHeight, setCurrentMaxHeight] = useState<number>()

        useLayoutEffect(() => {
            let maxHeight = 0
            let raf: number

            function handleResize() {
                items.forEach(item => {
                    const el = itemHeightRef.current.get(item.key)
                    if (!el) {
                        return
                    }
                    const oldMinHeight = el.style.minHeight
                    el.style.minHeight = ''
                    const { height } = el.getBoundingClientRect()
                    el.style.minHeight = oldMinHeight
                    maxHeight = Math.ceil(Math.max(maxHeight, height))
                })

                if (maxHeight !== currentMaxHeight) {
                    flushSync(() => {
                        setCurrentMaxHeight(maxHeight)
                    })
                }
            }

            const observer = new ResizeObserver(() => {
                raf = requestAnimationFrame(handleResize)
            })

            items.forEach(item => {
                const el = itemHeightRef.current.get(item.key)
                if (!el) {
                    return
                }
                observer.observe(el)
            })

            return () => {
                if (raf) {
                    cancelAnimationFrame(raf)
                }

                if (observer) {
                    observer.disconnect()
                }
            }
        }, [currentMaxHeight, items])

        const widthSize: SizeConfigure = useMemo(() => {
            if (isFitArrangement) {
                return {
                    size: 'auto'
                }
            }

            if (itemWidth) {
                return {
                    size: itemWidth,
                    unit: 'px'
                }
            }

            return {
                size: 'fill'
            }
        }, [isFitArrangement, itemWidth])

        const recordIds = useMemo(() => sortedRecordPoints.map(rp => rp.id), [sortedRecordPoints])

        const sensors = useSensors(
            useSensor(MouseSensor, {
                activationConstraint: { distance: 8 }
            }),
            useSensor(TouchSensor, {
                activationConstraint: {
                    delay: 250,
                    tolerance: 5
                }
            })
        )

        const handleDragEnd = useCallback(
            (event: DragEndEvent) => {
                const { active, over } = event
                if (!over || active.id === over.id || !enableDrag || !sortId || !onUpdateRecord) {
                    return
                }

                const draggedId = String(active.id)
                const activeIndex = recordIds.indexOf(draggedId)
                const overIndex = recordIds.indexOf(String(over.id))
                if (activeIndex === -1 || overIndex === -1) {
                    return
                }

                const reordered = arrayMove(recordIds, activeIndex, overIndex)

                const pool = store.get(recordPoolAtom)
                const siblings = reordered
                    .filter(rid => rid !== draggedId)
                    .map(rid => {
                        const record = pool.get(getRecordPoolKey(dsId, rid))
                        return record ? { id: rid, record } : null
                    })
                    .filter((item): item is { id: string; record: RecordLikeProtocol } => Boolean(item))

                const insertIndex = reordered.indexOf(draggedId)
                const siblingsBeforeDragged = reordered.slice(0, insertIndex).filter(rid => rid !== draggedId).length

                const newSortValue = calculateNewSortValue(siblings, siblingsBeforeDragged, sortId)

                const updateContent: RecordLikeProtocol['content'] = {
                    [sortId]: {
                        ...schema[sortId],
                        value: newSortValue
                    }
                }

                onUpdateRecord(draggedId, updateContent)
            },
            [enableDrag, sortId, onUpdateRecord, recordIds, store, recordPoolAtom, schema, dsId]
        )

        const { selectedNodes } = useFlowLayoutContext()
        const isBlockSelected = useMemo(
            () => selectedNodes?.some(item => item.scope === node.scope && item.id === node.id),
            [selectedNodes, node.scope, node.id]
        )
        const dragActive = readonly ? true : isBlockSelected

        const renderContent = (
            <Root
                gap={gap}
                style={{
                    position: 'relative',
                    height: currentMaxHeight ? currentMaxHeight / scale : undefined,
                    width: virtualizer.getTotalSize() / scale
                }}
            >
                {items.map(item => {
                    const recordId = effectiveRecordPoints[item.index].id
                    const itemContent = (
                        <Item
                            ref={r => {
                                itemHeightRef.current.set(item.key, r)
                            }}
                            data-record-id={recordId}
                            recordId={recordId}
                            recordIndexPath={String(item.index)}
                            readonly={readonly}
                            data={{
                                id: `${blockId}/${recordId}`,
                                scope: node.scope,
                                type: 'container',
                                disabled: readonly || item.index !== 0,
                                virtual: true,
                                data: node.data && {
                                    design: node.data.design,
                                    size: {
                                        width: widthSize,
                                        height: {
                                            size: 'auto'
                                        },
                                        minHeight: {
                                            size: 'fill'
                                        }
                                    },
                                    layout: node.data.layout,
                                    position: node.data.position
                                },
                                children: setDeadCopyNode(node.children || [], item.index === 0, blockId)
                            }}
                            title={title}
                            onRecordClick={onRecordClick}
                        />
                    )

                    if (enableDrag) {
                        return (
                            <SortableHorizontalItem
                                key={String(item.key)}
                                id={recordId}
                                disabled={!dragActive}
                                measureRef={virtualizer.measureElement}
                                dataIndex={item.index}
                                dataViewItem={item.index}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    transform: `translateX(${item.start}px)`
                                }}
                            >
                                {itemContent}
                            </SortableHorizontalItem>
                        )
                    }

                    return (
                        <div
                            key={String(item.key)}
                            ref={virtualizer.measureElement}
                            data-index={item.index}
                            data-view-item={item.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                transform: `translateX(${item.start}px)`,
                                cursor: 'pointer'
                            }}
                        >
                            {itemContent}
                        </div>
                    )
                })}
            </Root>
        )

        if (enableDrag) {
            return (
                <ScrollArea ref={mergeRefs([innerRef, ref])} id={anchorId} {...rest}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={recordIds} disabled={!dragActive} strategy={horizontalListSortingStrategy}>
                            {renderContent}
                        </SortableContext>
                    </DndContext>
                </ScrollArea>
            )
        }

        return (
            <ScrollArea ref={mergeRefs([innerRef, ref])} id={anchorId} {...rest}>
                {renderContent}
            </ScrollArea>
        )
    })
)
